import { ComponentPropsWithoutRef, CSSProperties, PropsWithChildren } from 'react';
import styled from 'styled-components';
import tokens from './tokens';

/**
 * Maps a `tokens.colors` key to the CSS custom property name lis-ds uses for
 * the same colour (`lis-ds/styles/lis-tokens.css`). Most keys differ only by
 * kebab-casing, but a handful of mockup colours live under lis-ds's
 * `--lis-color-wizard-*` prefix, and `brown`/`red` map to lis-ds's more
 * generic `tan`/`secondary` names — so this needs an explicit table rather
 * than a blanket `toKebabCase`.
 */
const CSS_COLOR_VAR_NAME: Record<keyof typeof tokens.colors, string> = {
  primary: 'primary',
  brown: 'tan',
  red: 'secondary',
  neutral: 'neutral',
  pale: 'pale',
  background: 'background',
  surface: 'surface',
  borderSubtle: 'border-subtle',
  inputDisabledBg: 'input-disabled-bg',
  textPrimary: 'text-primary',
  textSecondary: 'text-secondary',
  textDisabled: 'text-disabled',
  divider: 'divider',
  error: 'error',
  errorDark: 'error-dark',
  success: 'success',
  successLight: 'success-light',
  successDark: 'success-dark',
  warning: 'warning',
  warningDark: 'warning-dark',
  warningTint: 'warning-tint',
  info: 'info',
  infoDark: 'info-dark',
  infoTint: 'info-tint',
  errorTint: 'error-tint',
  successTint: 'success-tint',
  chipDefaultBg: 'chip-default-bg',
  chipDefaultText: 'chip-default-text',
  yellowDark: 'wizard-yellow-dark',
  yellowPale: 'wizard-yellow-pale',
  brownSoft: 'wizard-brown-soft',
  brownDark: 'wizard-brown-dark',
  muted: 'wizard-muted',
  fieldBorder: 'wizard-field-border',
  disc: 'wizard-disc',
  smartId: 'wizard-smart-id',
  mobileId: 'wizard-mobile-id',
  surfaceMuted: 'wizard-surface-muted',
  noteBg: 'wizard-note-bg',
  // lis-ds home: palette.wizard.backdrop.
  backdrop: 'wizard-backdrop',
};

/**
 * `stepDisplayLg` -> `step-display-lg`, `threeQuarter` -> `three-quarter`.
 * Matches lis-ds's own CSS custom property naming so the tokens it
 * generates from `theme.js` land on the same names this file emits.
 *
 * The regex is intentionally identical to lis-ds's own `tokens.mjs`: it
 * splits only at a lowercase/digit-to-uppercase boundary, with no separate
 * digit handling, so a step like `display2xl` emits
 * `--lis-font-size-display2xl` on both sides — never a `display-2xl` split
 * on one side and not the other.
 */
const kebab = (k: string) => k.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * Flattens every design token into a `--lis-*` CSS custom property, matching
 * lis-ds's exact names (`lis-ds/styles/lis-tokens.css`) so this stays a
 * drop-in replacement once the mockup-only colours land upstream. Declared
 * as an inline `style` on `LisScope`'s own element (not `createGlobalStyle`)
 * so nothing leaks into the MUI app shell or across tests that don't mount
 * `LisScope`/`LisUiRoot`.
 *
 * `--lis-font-weight-<step>` (one per `font.scale` step, e.g. `-sm`, `-base`)
 * and `--lis-font-weight-<name>` (one per `font.weight` name, e.g.
 * `-regular`, `-bold`) share the same `--lis-font-weight-` prefix by design:
 * the two key spaces (scale step names vs. weight names) are disjoint, so
 * there is no collision even though both loops below write into the same
 * `vars` map under that prefix.
 */
function buildCssVars(): CSSProperties {
  const vars: Record<string, string> = {};

  Object.entries(tokens.colors).forEach(([key, value]) => {
    const varName = CSS_COLOR_VAR_NAME[key as keyof typeof tokens.colors];
    vars[`--lis-color-${varName}`] = value;
  });
  Object.entries(tokens.space).forEach(([step, value]) => {
    vars[`--lis-space-${kebab(step)}`] = value;
  });
  Object.entries(tokens.shadow).forEach(([step, value]) => {
    vars[`--lis-shadow-${step}`] = value;
  });
  vars['--lis-font-family'] = tokens.font.family;
  Object.entries(tokens.font.scale).forEach(([step, s]) => {
    const name = kebab(step);
    vars[`--lis-font-size-${name}`] = s.size;
    vars[`--lis-line-height-${name}`] = s.lineHeight;
    vars[`--lis-font-weight-${name}`] = s.weight;
    if ('letterSpacing' in s) {
      vars[`--lis-letter-spacing-${name}`] = s.letterSpacing;
    }
    if ('textTransform' in s) {
      vars[`--lis-text-transform-${name}`] = s.textTransform;
    }
  });
  Object.entries(tokens.font.weight).forEach(([name, value]) => {
    vars[`--lis-font-weight-${name}`] = value;
  });
  vars['--lis-radius'] = tokens.radius;
  vars['--lis-radius-chip'] = tokens.radiusChip;
  vars['--lis-radius-lg'] = tokens.radiusLg;

  return vars as CSSProperties;
}

const lisCssVars = buildCssVars();

const ScopeRoot = styled.div`
  font-family: ${p => p.theme.font.family};
  color: ${p => p.theme.colors.brown};
`;

export type LisScopeProps = Omit<ComponentPropsWithoutRef<'div'>, 'style'>;

/**
 * Re-establishes the design system's typography and `--lis-*` custom
 * properties on its own DOM subtree. `LisUiRoot` mounts this once at the
 * app root, but CSS custom properties only inherit through the DOM tree —
 * not the React tree — so anything rendered outside `LisUiRoot`'s own DOM
 * subtree (most notably `Dialog`'s `createPortal` output, which lands in a
 * node appended directly to `document.body`) needs its own `LisScope`
 * wrapper to see the same variables and fonts/colours again.
 */
export function LisScope({ children, ...rest }: PropsWithChildren<LisScopeProps>) {
  return (
    <ScopeRoot style={lisCssVars} {...rest}>
      {children}
    </ScopeRoot>
  );
}

export default LisScope;
